import React from 'react';
import Input from 'rsuite/esm/Input';
import Toggle from 'rsuite/esm/Toggle';
import type {
    AddonModerationAdminSettings,
    AddonModerationMode,
    LlmConductorProviderId,
    LlmProviderPublic,
} from '../../../../shared/addon_moderation';
import { LLM_CONDUCTOR_PROVIDER_IDS } from '../../../../shared/addon_moderation';
import { LangBlock, ShowNotify } from '../../global';
import { WebStorage } from '../../../storage';
import {
    AdminButton,
    AdminCard,
    AdminEmpty,
    AdminFormField,
    AdminFormGrid,
    AdminSection,
} from './admin.ui';

const MODE_OPTIONS: {
    id: AddonModerationMode;
    title:
        | 'AdminLlmModeOff'
        | 'AdminLlmModePrecheck'
        | 'AdminLlmModePreferAuto'
        | 'AdminLlmModeAuto';
    desc:
        | 'AdminLlmModeOffDesc'
        | 'AdminLlmModePrecheckDesc'
        | 'AdminLlmModePreferAutoDesc'
        | 'AdminLlmModeAutoDesc';
}[] = [
    { id: 'off', title: 'AdminLlmModeOff', desc: 'AdminLlmModeOffDesc' },
    { id: 'precheck', title: 'AdminLlmModePrecheck', desc: 'AdminLlmModePrecheckDesc' },
    { id: 'prefer_auto', title: 'AdminLlmModePreferAuto', desc: 'AdminLlmModePreferAutoDesc' },
    { id: 'auto', title: 'AdminLlmModeAuto', desc: 'AdminLlmModeAutoDesc' },
];

const emptyDraft = (id: LlmConductorProviderId): LlmProviderPublic & { apiKey: string } => ({
    id,
    label: '',
    model: '',
    baseUrl: '',
    enabled: true,
    priority: 100,
    dailyLimit: 0,
    hasApiKey: false,
    apiKeyMasked: '',
    apiKey: '',
});

export const AdminLlmModeration = () => {
    const [, , LangString] = LangBlock();
    const [loaded, setLoaded] = React.useState(false);
    const [error, setError] = React.useState('');
    const [mode, setMode] = React.useState<AddonModerationMode>('off');
    const [extraPrompt, setExtraPrompt] = React.useState('');
    const [systemPrompt, setSystemPrompt] = React.useState('');
    const [providers, setProviders] = React.useState<LlmProviderPublic[]>([]);
    const [available, setAvailable] = React.useState<AddonModerationAdminSettings['availableProviders']>([]);
    const [savingSettings, setSavingSettings] = React.useState(false);
    const [draftId, setDraftId] = React.useState<LlmConductorProviderId>('gemini');
    const [draft, setDraft] = React.useState(emptyDraft('gemini'));
    const [savingProvider, setSavingProvider] = React.useState(false);
    const [testing, setTesting] = React.useState(false);
    const [liveModels, setLiveModels] = React.useState<string[]>([]);
    const [loadingModels, setLoadingModels] = React.useState(false);
    const modelsRequest = React.useRef(0);

    const applyPayload = (res: AddonModerationAdminSettings) => {
        setMode(res.mode);
        setExtraPrompt(res.extraPrompt || '');
        setSystemPrompt(res.systemPrompt || '');
        setProviders(res.providers || []);
        setAvailable(res.availableProviders || []);
    };

    const load = React.useCallback(() => {
        return WebStorage.ApiRequest('/api/admin/llm/moderation').then((res) => {
            if (res.status) {
                applyPayload(res as AddonModerationAdminSettings);
                setError('');
            } else {
                setError(res.error_message || LangString('AdminLlmSettingsLoadFailed'));
            }

            setLoaded(true);
        });
    }, [LangString]);

    React.useEffect(() => {
        load();
    }, [load]);

    const applyDraft = (id: LlmConductorProviderId, item?: LlmProviderPublic) => {
        setDraftId(id);
        setDraft(item ? { ...item, apiKey: '' } : emptyDraft(id));
        setLiveModels([]);
    };

    const loadModels = (id: LlmConductorProviderId, apiKey = '', silent = false) => {
        const requestId = ++modelsRequest.current;

        setLoadingModels(true);
        WebStorage.ApiRequestPost('/api/admin/llm/providers/models', {
            provider: id,
            ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        }).then((res) => {
            if (requestId !== modelsRequest.current) {
                return;
            }

            setLoadingModels(false);

            if (!res.status || !res.ok) {
                if (!silent) {
                    ShowNotify(
                        LangString('AdminLlmProviderModel'),
                        String(res.error || res.error_message || LangString('AdminLlmProviderLoadModelsFailed')),
                        'error',
                    );
                }

                return;
            }

            setLiveModels(
                Array.isArray(res.models)
                    ? res.models.map((item: unknown) => String(item || '').trim()).filter(Boolean)
                    : [],
            );
        });
    };

    const editProvider = (item: LlmProviderPublic) => {
        applyDraft(item.id, item);

        if (item.hasApiKey) {
            loadModels(item.id, '', true);
        }
    };

    const saveSettings = () => {
        if (savingSettings) {
            return;
        }

        setSavingSettings(true);
        WebStorage.ApiRequestPost('/api/admin/llm/moderation', {
            mode,
            extraPrompt,
        }).then((res) => {
            setSavingSettings(false);

            if (!res.status) {
                ShowNotify(
                    LangString('AdminLlmModerationTitle'),
                    res.error_message || LangString('AdminLlmSettingsSaveFailed'),
                    'error',
                );

                return;
            }

            applyPayload(res as AddonModerationAdminSettings);
            ShowNotify(
                LangString('AdminLlmModerationTitle'),
                LangString('AdminLlmModerationSaved'),
                'success',
            );
        });
    };

    const saveProvider = () => {
        if (savingProvider) {
            return;
        }

        setSavingProvider(true);
        WebStorage.ApiRequestPost('/api/admin/llm/providers/save', {
            provider: draftId,
            label: draft.label,
            model: draft.model,
            enabled: draft.enabled ? '1' : '0',
            priority: String(draft.priority),
            dailyLimit: String(draft.dailyLimit),
            ...(draft.apiKey.trim() ? { apiKey: draft.apiKey.trim() } : {}),
        }).then((res) => {
            setSavingProvider(false);

            if (!res.status) {
                ShowNotify(
                    LangString('AdminLlmProvidersTitle'),
                    res.error_message || LangString('AdminLlmProviderSaveFailed'),
                    'error',
                );

                return;
            }

            ShowNotify(
                LangString('AdminLlmProvidersTitle'),
                LangString('AdminLlmProviderSaved'),
                'success',
            );
            setDraft((prev) => ({ ...prev, apiKey: '' }));
            load();
            loadModels(draftId, '', true);
        });
    };

    const deleteProvider = (id: string) => {
        WebStorage.ApiRequestPost('/api/admin/llm/providers/delete', { provider: id }).then((res) => {
            if (!res.status) {
                ShowNotify(
                    LangString('AdminLlmProvidersTitle'),
                    res.error_message || LangString('AdminLlmProviderDeleteFailed'),
                    'error',
                );

                return;
            }

            ShowNotify(
                LangString('AdminLlmProvidersTitle'),
                LangString('AdminLlmProviderDeleted'),
                'success',
            );

            if (draftId === id) {
                applyDraft('gemini');
            }

            load();
        });
    };

    const testProvider = (real: boolean) => {
        if (testing) {
            return;
        }

        setTesting(true);
        WebStorage.ApiRequestPost('/api/admin/llm/providers/test', {
            provider: draftId,
            model: draft.model,
            real: real ? '1' : '0',
            ...(draft.apiKey.trim() ? { apiKey: draft.apiKey.trim() } : {}),
        }).then((res) => {
            setTesting(false);

            if (!res.status) {
                ShowNotify(
                    LangString('AdminLlmProviderTest'),
                    res.error_message || LangString('AdminLlmProviderTestFail'),
                    'error',
                );

                return;
            }

            if (res.ok) {
                ShowNotify(
                    LangString('AdminLlmProviderTest'),
                    LangString(
                        'AdminLlmProviderTestOk',
                        String(res.ms || 0),
                        String(res.preview || res.modelsCount || ''),
                    ),
                    'success',
                );

                return;
            }

            ShowNotify(
                LangString('AdminLlmProviderTest'),
                String(res.error || LangString('AdminLlmProviderTestFail')),
                'error',
            );
        });
    };

    const meta = available.find((item) => item.id === draftId);
    const modelOptions = [
        ...new Set(
            [...(meta?.suggestedModels || []), ...liveModels, draft.model]
                .map((item) => item.trim())
                .filter(Boolean),
        ),
    ];

    return (
        <>
            <AdminSection
                title={LangString('AdminLlmModerationTitle')}
                subtitle={LangString('AdminLlmModerationSubtitle')}
                actions={
                    <AdminButton variant="primary" disabled={savingSettings} onClick={saveSettings}>
                        {savingSettings ? LangString('AdminSaving') : LangString('AdminSave')}
                    </AdminButton>
                }
            >
                {!loaded ? (
                    <AdminEmpty message={LangString('AdminLoading')} />
                ) : error ? (
                    <AdminEmpty message={error} />
                ) : (
                    <>
                        <div className="admin-llm-modes">
                            {MODE_OPTIONS.map((option) => (
                                <label
                                    key={option.id}
                                    className={`admin-llm-mode${
                                        mode === option.id ? ' admin-llm-mode--active' : ''
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="addon-moderation-mode"
                                        checked={mode === option.id}
                                        onChange={() => setMode(option.id)}
                                    />
                                    <span>
                                        <strong>{LangString(option.title)}</strong>
                                        <em>{LangString(option.desc)}</em>
                                    </span>
                                </label>
                            ))}
                        </div>
                        <AdminFormField label={LangString('AdminLlmSystemPrompt')} wide>
                            <Input as="textarea" rows={14} value={systemPrompt} readOnly />
                            <p className="admin-llm-hint">{LangString('AdminLlmSystemPromptHint')}</p>
                        </AdminFormField>
                        <AdminFormField label={LangString('AdminLlmExtraPrompt')} wide>
                            <Input
                                as="textarea"
                                rows={6}
                                value={extraPrompt}
                                onChange={(value) => setExtraPrompt(String(value || ''))}
                            />
                            <p className="admin-llm-hint">{LangString('AdminLlmExtraPromptHint')}</p>
                        </AdminFormField>
                    </>
                )}
            </AdminSection>

            <AdminSection
                title={LangString('AdminLlmProvidersTitle')}
                subtitle={LangString('AdminLlmProvidersSubtitle')}
            >
                {providers.length === 0 ? (
                    <AdminEmpty message={LangString('AdminLlmNoProviders')} />
                ) : (
                    <div className="admin-llm-provider-list">
                        {providers.map((item) => (
                            <AdminCard
                                key={item.id}
                                title={`${item.label || item.id}${item.enabled ? '' : ` (${LangString('AdminLlmProviderDisabled')})`}`}
                            >
                                <p className="admin-llm-card__desc">
                                    {item.model ||
                                        available.find((entry) => entry.id === item.id)
                                            ?.suggestedModels?.[0] ||
                                        '—'}{' '}
                                    ·{' '}
                                    {item.hasApiKey
                                        ? item.apiKeyMasked
                                        : LangString('AdminLlmProviderNoKey')}
                                </p>
                                <div className="admin-llm-url__actions">
                                    <AdminButton size="sm" onClick={() => editProvider(item)}>
                                        {LangString('AdminEdit')}
                                    </AdminButton>
                                    <AdminButton
                                        size="sm"
                                        variant="danger"
                                        onClick={() => deleteProvider(item.id)}
                                    >
                                        {LangString('AdminDelete')}
                                    </AdminButton>
                                </div>
                            </AdminCard>
                        ))}
                    </div>
                )}

                <AdminCard title={LangString('AdminLlmProviderForm')}>
                    <AdminFormGrid>
                        <AdminFormField label={LangString('AdminLlmProviderId')}>
                            <select
                                className="admin-llm-select"
                                value={draftId}
                                onChange={(event) => {
                                    const id = event.target.value as LlmConductorProviderId;
                                    const existing = providers.find((item) => item.id === id);

                                    applyDraft(id, existing);

                                    if (existing?.hasApiKey) {
                                        loadModels(id, '', true);
                                    }
                                }}
                            >
                                {LLM_CONDUCTOR_PROVIDER_IDS.map((id) => (
                                    <option key={id} value={id}>
                                        {available.find((item) => item.id === id)?.label || id}
                                    </option>
                                ))}
                            </select>
                        </AdminFormField>
                        <AdminFormField label={LangString('AdminLlmProviderLabel')}>
                            <Input
                                value={draft.label}
                                onChange={(value) => setDraft((prev) => ({ ...prev, label: String(value || '') }))}
                            />
                        </AdminFormField>
                        <AdminFormField label={LangString('AdminLlmProviderModel')} wide>
                            <div className="admin-llm-model-row">
                                <select
                                    className="admin-llm-select"
                                    value={draft.model}
                                    onChange={(event) =>
                                        setDraft((prev) => ({ ...prev, model: event.target.value }))
                                    }
                                >
                                    <option value="">{LangString('AdminLlmProviderModelDefault')}</option>
                                    {modelOptions.map((model) => (
                                        <option key={model} value={model}>
                                            {model}
                                        </option>
                                    ))}
                                </select>
                                <AdminButton
                                    size="sm"
                                    disabled={loadingModels}
                                    onClick={() => loadModels(draftId, draft.apiKey)}
                                >
                                    {loadingModels
                                        ? LangString('AdminLoading')
                                        : LangString('AdminLlmProviderLoadModels')}
                                </AdminButton>
                            </div>
                            <p className="admin-llm-hint">{LangString('AdminLlmProviderModelHint')}</p>
                        </AdminFormField>
                        <AdminFormField label={LangString('AdminLlmProviderApiKey')} wide>
                            <Input
                                type="password"
                                value={draft.apiKey}
                                placeholder={
                                    draft.hasApiKey
                                        ? LangString('AdminLlmKeyKeep', draft.apiKeyMasked)
                                        : ''
                                }
                                onChange={(value) => setDraft((prev) => ({ ...prev, apiKey: String(value || '') }))}
                            />
                        </AdminFormField>
                        <AdminFormField label={LangString('AdminLlmProviderPriority')}>
                            <Input
                                type="number"
                                value={String(draft.priority)}
                                onChange={(value) =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        priority: parseInt(String(value || '0'), 10) || 0,
                                    }))
                                }
                            />
                        </AdminFormField>
                        <AdminFormField label={LangString('AdminLlmProviderDailyLimit')}>
                            <Input
                                type="number"
                                value={String(draft.dailyLimit)}
                                onChange={(value) =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        dailyLimit: parseInt(String(value || '0'), 10) || 0,
                                    }))
                                }
                            />
                        </AdminFormField>
                        <AdminFormField label={LangString('AdminLlmProviderEnabled')}>
                            <Toggle
                                checked={draft.enabled}
                                onChange={(checked) => setDraft((prev) => ({ ...prev, enabled: checked }))}
                            />
                        </AdminFormField>
                    </AdminFormGrid>
                    <div className="admin-llm-url__actions">
                        <AdminButton variant="primary" disabled={savingProvider} onClick={saveProvider}>
                            {savingProvider ? LangString('AdminSaving') : LangString('AdminLlmProviderSave')}
                        </AdminButton>
                        <AdminButton disabled={testing} onClick={() => testProvider(false)}>
                            {testing ? '...' : LangString('AdminLlmProviderTest')}
                        </AdminButton>
                        <AdminButton disabled={testing} onClick={() => testProvider(true)}>
                            {testing ? '...' : LangString('AdminLlmProviderTestReal')}
                        </AdminButton>
                    </div>
                </AdminCard>
            </AdminSection>
        </>
    );
};
